import { Dialog, Grid } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { CancelButtonBlack } from '@tassat/button';
// import { AssetTicker, FailedTransactionWorkflowCodes, TransactionStatusCode, TransactionTypeString }
// from '@tassat/fred-extended-definitions';
import React from 'react';

// import TransactConfirmation from 'Containers/client/forms/transactConfirmation';
// import { registerTimeout } from 'Lib/serverTimeout';
import { DEFAULT_ITEMS_PER_PAGE } from 'Lib/constant';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppDispatch, useAppSelector } from 'Reducers/store/hooks';
import { getAssetFromTicker } from 'Selectors/assets';
// import { getNetAssetValuesAsCurCode } from 'Selectors/netAssetValues';
import { getCurrentRole, getPledgeDetail, getUser } from 'Selectors/common';
import { getKillSwitchOn } from 'Selectors/killswitch';
import { getPledgeById } from 'Selectors/pledges';
import { getUserAccountByPledgeParticipantAccount } from 'Selectors/userAccounts';

import { PledgeContextWorkflowConsumer, PledgeContextWorkflowContext } from './pledgeContextWorkflow';
import { PledgeResultModal } from '../pledgeResultModal';
import PledgeDetailsModalScreens from './pledgeDetailsModalScreens/pledgeDetailsModalScreens';
import PledgeDetails from '../../pledgeDetails/pledgeDetails';

export interface PledgeDetailsModalProps extends WithStyles<typeof muiStyles> {
    selectedPledgeId: string;
    open: boolean;
    onClose: () => void;
    accountId?: string;
}

const muiStyles = (theme: ApplicationMuiTheme) => ({
    root: {
        minWidth: '560px',
        padding: '1.5rem'
    },
    detailsBorder: {
        borderTop: `5px solid ${theme?.app.clientTheme.colors.primaryColor ||
        '#375073'}`
    },
    approvalBorder: {
        borderTop: `12px solid ${theme?.app?.applicationColors.successDark ||
        '#5AAC01'}`
    },
    declineBorder: {
        borderTop: `12px solid ${theme?.app?.applicationColors.failureDark ||
        '#AD3D4B'}`
    },
    approvalHeader: {
        alignSelf: 'center',
        fontSize: '2rem',
        fontWeight: 'bold' as const,
        color: theme?.app?.applicationColors.successDark ||
            '#5AAC01'
    },
    declineHeader: {
        alignSelf: 'center',
        fontSize: '2rem',
        fontWeight: 'bold' as const,
        color: theme?.app?.applicationColors.failureDark ||
            '#AD3D4B'
    },
    cancelIcon: {
        position: 'absolute' as const,
        top: 18,
        right: 18
    },
    paperWidthXl: { width: '80%' },
    paperWidthM: { width: '40%' },
    '@media (max-width: 1360px)': {
        paperWidthXl: { width: '700px' },
        paperWidthM: { width: '400px' }
    }
});

export type ModalScreen = 'details' | 'addOn' | 'release' | 'reclaim' | 'approve' | 'decline' | 'cancel';

function PledgeDetailsModalContent(props: PledgeDetailsModalProps) {
    const {
        classes,
        selectedPledgeId,
        open,
        onClose,
        accountId
    } = props;

    const { pledgeContextWorkflowState, dispatchPledgeContextWorkflowAction } = React.useContext(PledgeContextWorkflowContext)!;
    const {
        modalScreen,
        pledgeActionInProgress,
        pledgeType,
        modalPledgeValues,
        modalAction
    } = pledgeContextWorkflowState;

    const dispatch = useAppDispatch();
    const killSwitch = useAppSelector(getKillSwitchOn);
    const currentUser = useAppSelector(getUser);
    const pledgeDetail = useAppSelector(getPledgeDetail);
    const selectedPledge = useAppSelector((state) => getPledgeById(state, selectedPledgeId));
    const userPledgorAccount = useAppSelector((state) => (pledgeDetail ?
        getUserAccountByPledgeParticipantAccount(state, pledgeDetail.PledgingAccountAddress) : undefined));
    const userPledgeeAccount = useAppSelector((state) => (pledgeDetail ?
        getUserAccountByPledgeParticipantAccount(state, pledgeDetail.PledgeParticipant?.[0]?.PledgeParticipantAccountAddress) : undefined));
    const asset = useAppSelector((state) => (selectedPledge ? getAssetFromTicker(state, selectedPledge.Ticker) : undefined));
    const currentRole = useAppSelector(getCurrentRole);

    React.useEffect(() => {
        if (!selectedPledge) return;

        const getPledgeDetail = async () => {
            try {
                dispatchPledgeContextWorkflowAction({
                    type: 'SET_SELECTED_PLEDGE',
                    payload: selectedPledge
                });
                const getPledgeDetailParams = {
                    Condition: `PledgeId|EQ|${selectedPledge.PledgeId}`,
                    RecordPerPage: DEFAULT_ITEMS_PER_PAGE,
                    PageNo: 1,
                    Sort: 'PledgeTransactionAuditTime|DESC'
                };
                await Worker.getInstance()
                    .getCollateralDetail(getPledgeDetailParams);

                dispatchPledgeContextWorkflowAction({
                    type: 'SET_ACCOUNT_ID',
                    payload: accountId
                });
            } catch (error) {
                console.error(error);
            }
        };

        getPledgeDetail();
    }, [selectedPledgeId]);

    const changeModalScreen = (
        screen: ModalScreen
    ) => {
        dispatchPledgeContextWorkflowAction({
            type: 'CHANGE_MODAL_SCREEN',
            payload: { screen }
        });
    };

    if (!selectedPledgeId || !selectedPledge || !pledgeDetail || !asset) return <div />;

    const handleClose = (): void => {
        changeModalScreen('details');
        onClose();
    };

    return (
        <>
            <Dialog
                classes={{ paperWidthXl: classes.paperWidthXl }}
                maxWidth='xl'
                onClose={handleClose}
                open={open}
                transitionDuration={{
                    enter: 0,
                    exit: 0
                }}
            >
                <Grid
                    className={`${classes.root} ${classes.detailsBorder}`}
                    container
                    direction='column'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        className={classes.cancelIcon}
                        item
                    >
                        <CancelButtonBlack
                            onClick={handleClose}
                        />
                    </Grid>
                    <PledgeDetails
                        asset={asset}
                        changeModalScreen={changeModalScreen}
                        currentRole={currentRole}
                        currentUser={currentUser}
                        killSwitch={killSwitch}
                        selectedPledge={pledgeDetail}
                        userPledgeeAccount={userPledgeeAccount}
                        userPledgorAccount={userPledgorAccount}
                    />
                </Grid>
            </Dialog>
            {modalScreen !== 'details' && (
                <PledgeDetailsModalScreens
                    modalScreen={modalScreen}
                    userPledgeeAccount={userPledgeeAccount}
                    userPledgorAccount={userPledgorAccount}
                />
            )}
            <PledgeResultModal
                actionType={modalAction}
                modalPledgeValues={modalPledgeValues}
                onClose={() => {
                    dispatchPledgeContextWorkflowAction({
                        type: 'SET_PLEDGE_ACTION_IN_PROGRESS',
                        payload: false
                    });
                    dispatchPledgeContextWorkflowAction({
                        type: 'SET_MODAL_ACTION',
                        payload: null
                    });
                    dispatch({ type: 'RESET_VALIDATION' });
                    changeModalScreen('details');
                }}
                open={pledgeActionInProgress}
                pledgeType={pledgeType}
                pledgeValidationType='pledgeCollateral'
            />
        </>
    );
}

export function PledgeDetailsModal(props: PledgeDetailsModalProps) {
    return (
        <PledgeContextWorkflowConsumer>
            <PledgeDetailsModalContent {...props} />
        </PledgeContextWorkflowConsumer>
    );
}

// const mapDispatchToProps = dispatch => ({
// TODO: ADD FOR COLLATERAL WHEN WE DO CLIENT - USE useAppDispatch HOOK
// resetTransactionValidation: () => dispatch({ type: 'RESET_VALIDATION' }),
// pledgeTimeoutError: () => dispatch({ type: 'PLEDGE_COLLATERAL_TIMEOUT' })
// });

export default withStyles(muiStyles)(PledgeDetailsModal);